<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb :title="page.title" :breadcrumbs="breadcrumbs"></BaseBreadcrumb>

         <v-row dense justify="center">
            <v-col cols="12" md="8">
               <v-card>
                  <v-toolbar dark color="primary">
                     <v-icon left>mdi-account-check-outline</v-icon>
                     <v-toolbar-title>Membership {{ roleLabel }} Response</v-toolbar-title>
                  </v-toolbar>

                  <v-card-text>
                     <v-alert type="info" dense text>
                        You have been selected as a <strong>{{ roleLabel }}</strong> for the following membership application.
                        Please review the details and approve or reject.
                     </v-alert>

                     <v-alert v-if="message" :type="messageType" dense text>
                        {{ message }}
                     </v-alert>

                     <v-card v-if="originator" outlined class="mb-4">
                        <v-card-text>
                           <div class="overline blue--text">Applicant Details</div>
                           <div class="text-h6 font-weight-bold">{{ originator.fullname }}</div>
                           <div class="body-2 grey--text">{{ originator.contactemail || '-' }}</div>
                           <div class="body-2 mt-1">MIP Member ID: <strong>{{ originator.memberid || 'Not assigned' }}</strong></div>
                           <div class="body-2 mt-1">Member Type: <strong>{{ applicantMemberType }}</strong></div>
                        </v-card-text>
                     </v-card>

                     <v-card v-if="currentmember" outlined class="mb-4">
                        <v-card-text>
                           <div class="overline blue--text">{{ roleLabel }} Details</div>
                           <div class="text-subtitle-1 font-weight-bold">{{ currentmember.fullname }}</div>
                           <div class="body-2 grey--text">{{ currentmember.contactemail || '-' }}</div>
                           <div class="body-2 mt-1">MIP Member ID: <strong>{{ currentmember.memberid || 'Not assigned' }}</strong></div>
                           <div class="body-2 mt-1">Member Type: <strong>{{ currentmember.membertype || '-' }}</strong></div>
                        </v-card-text>
                     </v-card>
                  </v-card-text>

                  <v-card-actions class="px-4 pb-4">
                     <v-spacer></v-spacer>
                     <v-btn text href="/dashboard">Back to Dashboard</v-btn>
                     <v-btn
                        color="error"
                        outlined
                        :loading="rejecting"
                        :disabled="submitting || !canRespond"
                        @click="submitResponse(false)"
                     >
                        <v-icon left>mdi-close</v-icon>Reject
                     </v-btn>
                     <v-btn
                        color="success"
                        dark
                        :loading="approving"
                        :disabled="submitting || !canRespond"
                        @click="submitResponse(true)"
                     >
                        <v-icon left>mdi-check</v-icon>Approve
                     </v-btn>
                  </v-card-actions>
               </v-card>
            </v-col>
         </v-row>
      </v-container>
   </dashboard-layout>
</template>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'

export default {
   name: 'MOMMembershipResponse',
   components: {
      DashboardLayout,
      BaseBreadcrumb,
   },
   props: {
      originator: Object,
      currentmember: Object,
      membertype: String,
      nomtype: String,
   },
   data() {
      return {
         page: { title: 'Membership Response' },
         breadcrumbs: [
            { text: 'Dashboard', disabled: false, to: '/dashboard' },
            { text: 'Membership Response', disabled: true },
         ],
         submitting: false,
         approving: false,
         rejecting: false,
         message: '',
         messageType: 'info',
      }
   },
   computed: {
      roleLabel() {
         return this.nomtype === 'seconder' ? 'Seconder' : 'Proposer'
      },
      applicantMemberType() {
         const type = this.membertype || (this.originator ? this.originator.membertype : '')
         if (!type) return '-'
         return type.indexOf('Member') === -1 ? `${type} Member` : type
      },
      canRespond() {
         return !!(this.originator && this.currentmember)
      },
   },
   mounted() {
      if (!this.originator) {
         this.setMessage('Membership application was not found.', 'error')
      } else if (!this.currentmember) {
         this.setMessage('Please login with the nominated member account before responding.', 'warning')
      }
   },
   methods: {
      setMessage(message, type) {
         this.message = message
         this.messageType = type
      },
      submitResponse(accepted) {
         if (!this.canRespond) {
            return
         }

         if (!window.confirm(`Confirm ${accepted ? 'APPROVE' : 'REJECT'}?`)) {
            return
         }

         this.submitting = true
         this.approving = accepted
         this.rejecting = !accepted
         this.setMessage('', 'info')

         const action = accepted ? 'accept' : 'reject'
         const targetPath = `/member/decl/${this.nomtype}/${action}`

         axios.post(targetPath, {
            userid: this.originator.userid,
            fromid: this.currentmember.userid,
         }).then((resp) => {
            const rtn = resp.data || {}
            if (rtn.status === 'OK') {
               this.setMessage(`Your ${this.roleLabel.toLowerCase()} response has been submitted.`, 'success')
               return
            }

            this.setMessage(rtn.error || 'Unable to submit response.', 'error')
         }).catch((err) => {
            const message = err.response && err.response.data && err.response.data.message
               ? err.response.data.message
               : 'Unable to submit response.'
            this.setMessage(message, 'error')
         }).finally(() => {
            this.submitting = false
            this.approving = false
            this.rejecting = false
         })
      },
   },
}
</script>
